import { ChartOptions, Plugin, Chart as ChartJS, ChartType } from 'chart.js'
import type { Chart as ChartInstance } from 'chart.js'

export const COUNT_AXIS = 'yCount'
export const PERCENT_AXIS = 'yPercent'
export const BAR_ALPHA = 0.25
export const METRIC_CHART_HEIGHT = 260
export const MANUAL_INTERVENTION_CHART_HEIGHT = 300

export const LINE_STYLE = {
  pointRadius: 3,
  pointHoverRadius: 4,
  borderWidth: 2,
  tension: 0.3,
} as const

// Series routinely share a value — all four sit at 0 on a quiet day, and a job with both proofs
// entered by hand puts POC and POD on the same point. A solid line would hide whatever is beneath
// it, so every series is dashed differently and the ones underneath show through the gaps.
export const SERIES_DASH_PATTERNS: number[][] = [
  [6, 3],
  [2, 2],
  [10, 4],
  [6, 3, 2, 3],
]

export const BAR_STYLE = {
  borderRadius: 2,
  categoryPercentage: 0.7,
  barPercentage: 0.9,
} as const

export const METRIC_CHART_SCALES: ChartOptions<'bar' | 'line'>['scales'] = {
  x: {
    grid: { display: false },
    ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 6 },
  },
  [COUNT_AXIS]: {
    type: 'linear',
    position: 'left',
    beginAtZero: true,
    title: { display: true, text: 'Number of jobs' },
    ticks: { precision: 0 },
  },
  [PERCENT_AXIS]: {
    type: 'linear',
    position: 'right',
    beginAtZero: true,
    grid: { drawOnChartArea: false },
    title: { display: true, text: '% of total jobs' },
    ticks: { callback: (value) => `${value}%` },
  },
}

// A factory, not a constant: chart.js's initOptions() writes `scales` and `plugins` onto the
// options object it is given, so every chart instance needs its own.
export const getManualInterventionChartOptions = (): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  scales: {
    x: {
      grid: { display: false },
      ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Number of jobs' },
      ticks: { precision: 0 },
    },
  },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        usePointStyle: true,
        boxWidth: 18,
        padding: 20,
      },
    },
  },
})

const LABEL_OFFSET = 4

export const createValueLabelsPlugin = <TType extends ChartType>(): Plugin<TType> => ({
  id: 'valueLabels',

  afterDatasetsDraw(chart) {
    const { ctx } = chart

    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.font = `600 11px ${ChartJS.defaults.font.family}`

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!chart.isDatasetVisible(datasetIndex)) {
        return
      }

      ctx.fillStyle = typeof dataset.backgroundColor === 'string' ? dataset.backgroundColor : '#000'

      chart.getDatasetMeta(datasetIndex).data.forEach((element, index) => {
        const value = dataset.data[index]

        if (typeof value !== 'number' || value === 0) {
          return
        }
        const formattedValue = dataset.type === 'line' ? `${value}%` : value.toLocaleString()
        const offsetY = dataset.type === 'bar' ? LABEL_OFFSET + 10 : LABEL_OFFSET
        ctx.fillText(formattedValue, element.x, element.y - offsetY)
      })
    })

    ctx.restore()
  },
})

const LEGEND_CHART_SPACING = 40

export const legendSpacing = {
  id: 'legend-chart-spacing',
  beforeInit(chart: ChartInstance) {
    const originalFit = chart.legend?.fit

    if (originalFit) {
      chart.legend.fit = function fit() {
        originalFit.bind(chart.legend)()
        this.height += LEGEND_CHART_SPACING
      }
    }
  },
}
